// Client publish orchestration: stage guide assignments, then POST them to
// /api/notion/push (the 3-tier UPSERT). Because slots carry their Notion pageId
// (they came FROM Notion), every change hits the tier-1 short-circuit — a single
// PATCH per slot — so publishing is fast and can't duplicate rows.
//
// NOTE: the push Function sets STATUS only when CREATING a brand-new page
// (multi_select ["PROPOSED"]); it never overwrites STATUS on an existing page,
// since STATUS is ops-owned. See push.ts + the open question in the handoff.

import type { GuideRef, PlanSlot } from "@/lib/load";

/** Mirrors functions/api/notion/push.ts PushChange (structural). */
export interface PushChange {
  peekTimeslotId: string;
  pageId?: string | null;
  market: string;
  tour: string;
  dateStart: string;
  startTimeLabel: string;
  startIso: string;
  title: string;
  guidePageId: string;
  guidePeekId: string;
}

export interface PushResult {
  peekTimeslotId: string;
  ok: boolean;
  tier: string;
  pageId?: string;
  error?: string;
}

export interface PushResponse {
  dryRun: boolean;
  ok: number;
  fail: number;
  byTier: Record<string, number>;
  results: PushResult[];
}

/** A pending (not yet published) assignment of a guide to a slot. */
export interface Staged {
  slot: PlanSlot;
  guide: GuideRef;
}

export function buildChange(slot: PlanSlot, guide: GuideRef): PushChange | null {
  // A missing Notion guide page id can't fill the Assigned Guide relation.
  if (!guide.pageId) return null;
  return {
    peekTimeslotId: slot.peekTimeslotId,
    pageId: slot.pageId || null,
    market: slot.market ?? "",
    tour: slot.tour,
    dateStart: slot.date,
    startTimeLabel: slot.startLabel,
    startIso: slot.startIso || slot.date,
    title: slot.title || `${slot.date} ${slot.hhmm} — ${slot.tour}`,
    guidePageId: guide.pageId,
    guidePeekId: guide.id,
  };
}

/** Split staged assignments into publishable changes and unmapped-guide rejects. */
export function prepareChanges(staged: Staged[]): {
  changes: PushChange[];
  rejected: Staged[];
} {
  const changes: PushChange[] = [];
  const rejected: Staged[] = [];
  for (const s of staged) {
    const c = buildChange(s.slot, s.guide);
    if (c) changes.push(c);
    else rejected.push(s);
  }
  return { changes, rejected };
}

export async function pushChanges(
  changes: PushChange[],
  dryRun: boolean,
): Promise<PushResponse> {
  const res = await fetch("/api/notion/push", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ changes, dryRun }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`push ${res.status}: ${body.slice(0, 200)}`);
  }
  return (await res.json()) as PushResponse;
}

// ── Peek guide-assign (guarded, add-only) ──────────────────────────────────
// Notion is the schedule source of truth; this best-effort mirrors a fresh
// assignment into Peek. It can only ADD to a slot with no guide — the Worker
// no-ops if the guide is already there and refuses if a different guide holds
// the slot (those must be changed in the Peek UI).
export interface PeekAssignResult {
  ok: boolean;
  decision?: "assigned" | "already-assigned" | "blocked-has-other-guide" | "unassign-disabled" | string;
  reason?: string;
  resolved?: { timeslotId: string; guideId: string; guideName: string };
  currentGuides?: { name: string; accountUserId: string }[];
}

export async function peekAssign(
  slotKey: string,
  guide: GuideRef,
  dry = false,
): Promise<PeekAssignResult> {
  const res = await fetch("/api/peek/assign", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slotKey, guideId: guide.id, guide: guide.peekName, dry }),
  });
  return (await res.json().catch(() => ({ ok: false, reason: `assign ${res.status}` }))) as PeekAssignResult;
}
